import { AlertCircle, CheckCircle2, Clock3, ExternalLink } from 'lucide-react';
import { type ReactNode, useEffect, useMemo, useRef } from 'react';
import useSWR from 'swr';

import { Alert, AlertDescription } from '@apps/web/components/ui/alert';
import { Badge } from '@apps/web/components/ui/badge';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from '@apps/web/components/ui/card';
import { Spinner } from '@apps/web/components/ui/spinner';
import { getApiClient } from '@apps/web/lib/api_client';
import { useApiQuery } from '@apps/web/swr_data_fetching/use_api_query';
import {
    type ApiError,
    type components,
    type Mode,
} from '@libraries/typescript/api_client';
import { assert } from '@libraries/typescript/utilities';

export type CaregiverSmsShadowComparison =
    components['schemas']['CaregiverSmsShadowComparison'];
export type CaregiverSmsShadowSummary =
    components['schemas']['CaregiverSmsShadowSummary'];

type ShadowStatus = CaregiverSmsShadowComparison['status'];

const STATUS_LABELS: Record<ShadowStatus, string> = {
    pending: 'Shadow pending',
    running: 'Shadow running',
    completed: 'Shadow ready',
    failed: 'Shadow failed',
    skipped: 'Shadow skipped',
};

const STATUS_DESCRIPTIONS: Record<ShadowStatus, string> = {
    pending: 'The comparison is waiting for a shadow worker.',
    running: 'The candidate run is still in progress.',
    completed: 'The candidate run completed.',
    failed: 'The candidate run failed. Its recorded errors appear below.',
    skipped: 'The worker skipped this selected comparison.',
};

const MAX_BATCH_MESSAGE_COUNT = 200;
const SHADOW_REFRESH_INTERVAL_MS = 5_000;
const BATCH_DISCOVERY_REFRESH_LIMIT = 6;

export function shadowRefreshInterval(
    comparison: CaregiverSmsShadowComparison | undefined,
): number {
    return comparison?.status === 'pending' || comparison?.status === 'running'
        ? SHADOW_REFRESH_INTERVAL_MS
        : 0;
}

export function shadowBatchRefreshInterval(
    response:
        | components['schemas']['CaregiverSmsShadowBatchResponse']
        | undefined,
    discoveryFetchCount: number,
): number {
    const hasActiveComparison = response?.items.some(
        (item) => item.status === 'pending' || item.status === 'running',
    );
    return hasActiveComparison ||
        discoveryFetchCount < BATCH_DISCOVERY_REFRESH_LIMIT
        ? SHADOW_REFRESH_INTERVAL_MS
        : 0;
}

export function useCaregiverSmsShadowSummaries(
    organizationId: string | null | undefined,
    sourceMessageIds: ReadonlyArray<string>,
    enabled: boolean,
    mode: Mode,
) {
    const uniqueSourceMessageIds = useMemo(
        () => Array.from(new Set(sourceMessageIds)).sort(),
        [sourceMessageIds],
    );
    const discoveryFetchCount = useRef(0);

    useEffect(() => {
        discoveryFetchCount.current = 0;
    }, [mode, organizationId, uniqueSourceMessageIds]);

    const queryKey =
        enabled && organizationId && uniqueSourceMessageIds.length > 0
            ? [
                  'post',
                  '/organizations/{organization_id}/caregiver-sms-shadow/by-message',
                  organizationId,
                  mode,
                  uniqueSourceMessageIds,
              ]
            : null;
    const apiClient = getApiClient();
    const query = useSWR<
        components['schemas']['CaregiverSmsShadowBatchResponse'],
        ApiError
    >(
        queryKey,
        async () => {
            if (!organizationId) {
                return { items: [] };
            }
            const requests: Array<
                Promise<
                    components['schemas']['CaregiverSmsShadowBatchResponse']
                >
            > = [];
            for (
                let index = 0;
                index < uniqueSourceMessageIds.length;
                index += MAX_BATCH_MESSAGE_COUNT
            ) {
                requests.push(
                    apiClient.post({
                        url: '/organizations/{organization_id}/caregiver-sms-shadow/by-message',
                        pathParams: { organization_id: organizationId },
                        body: {
                            message_ids: uniqueSourceMessageIds.slice(
                                index,
                                index + MAX_BATCH_MESSAGE_COUNT,
                            ),
                            mode,
                        },
                        mode,
                        reason: 'caregiver-sms-shadow-message-links',
                        idempotent: true,
                        rateLimitFailureMode: 'silent',
                    }),
                );
            }
            const responses = await Promise.all(requests);
            const response = {
                items: responses.flatMap((item) => item.items),
            };
            discoveryFetchCount.current += 1;
            return response;
        },
        {
            shouldRetryOnError: false,
            revalidateOnFocus: false,
            refreshInterval: (response) =>
                shadowBatchRefreshInterval(
                    response,
                    discoveryFetchCount.current,
                ),
        },
    );
    const summaries = useMemo(() => {
        const summariesByMessageId: Record<string, CaregiverSmsShadowSummary> =
            {};
        for (const summary of query.data?.items ?? []) {
            summariesByMessageId[summary.source_message_id] = summary;
        }
        return summariesByMessageId;
    }, [query.data]);

    return {
        ...query,
        hasRequestFailed: query.error !== undefined,
        summaries,
    };
}

export function useCaregiverSmsShadowComparison({
    organizationId,
    sourceMessageId,
    mode,
    enabled = true,
}: {
    readonly organizationId: string;
    readonly sourceMessageId: string;
    readonly mode: Mode;
    readonly enabled?: boolean;
}) {
    return useApiQuery({
        enabled,
        path: '/organizations/{organization_id}/caregiver-sms-shadow/{source_message_id}',
        method: 'get',
        pathParams: {
            organization_id: organizationId,
            source_message_id: sourceMessageId,
        },
        queryParams: { mode },
        mode,
        reason: 'caregiver-sms-shadow-comparison',
        refreshInterval: shadowRefreshInterval,
        select: (response) => response,
    });
}

function StatusIcon({ status }: { readonly status: ShadowStatus }) {
    switch (status) {
        case 'completed':
            return <CheckCircle2 className="size-3" />;
        case 'failed':
            return <AlertCircle className="size-3" />;
        case 'running':
            return <Spinner size="xs" />;
        case 'pending':
        case 'skipped':
            return <Clock3 className="size-3" />;
        default:
            return assert.never(status);
    }
}

export function CaregiverSmsShadowExecution({
    comparison,
    renderItem,
}: {
    readonly comparison: CaregiverSmsShadowComparison;
    readonly renderItem: (
        item: CaregiverSmsShadowComparison['transcript'][number],
    ) => ReactNode;
}) {
    return (
        <Card
            id="shadow-comparison"
            className="min-w-0 gap-4 py-4 shadow-none md:row-span-3 md:grid md:grid-cols-1 md:grid-rows-subgrid"
        >
            <CardHeader className="pb-2">
                <CardTitle className="text-base">Shadow execution</CardTitle>
                <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="outline">
                        <StatusIcon status={comparison.status} />
                        {STATUS_LABELS[comparison.status]}
                    </Badge>
                    <Badge variant="secondary">Actions not executed</Badge>
                </div>
            </CardHeader>
            <CardContent className="min-w-0 space-y-4">
                <div className="space-y-1">
                    <h3 className="text-sm font-medium">Hypothetical reply</h3>
                    <p
                        data-testid="shadow-hypothetical-reply"
                        className="rounded bg-blue-50 p-2 text-sm break-words whitespace-pre-wrap dark:bg-blue-950/50"
                    >
                        {comparison.candidate_outcome.reply ??
                            (comparison.candidate_outcome.decision?.kind ===
                            'no_reply'
                                ? 'The candidate chose silence.'
                                : 'No candidate reply recorded.')}
                    </p>
                    {comparison.candidate_outcome.decision ? (
                        <p className="text-muted-foreground text-xs">
                            Decision:{' '}
                            {comparison.candidate_outcome.decision.kind}
                        </p>
                    ) : null}
                </div>
            </CardContent>
            <CardContent className="min-w-0 space-y-4">
                <p className="text-muted-foreground text-xs">
                    {STATUS_DESCRIPTIONS[comparison.status]} Uses current data;
                    proposed changes are not reflected in later reads.
                </p>
                <dl className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                        <dt>Model</dt>
                        <dd>{comparison.candidate_outcome.model}</dd>
                    </div>
                    <div>
                        <dt>Duration</dt>
                        <dd>
                            {comparison.timings.duration_ms === null
                                ? 'Not recorded'
                                : `${comparison.timings.duration_ms} ms`}
                        </dd>
                    </div>
                    <div>
                        <dt>Cost</dt>
                        <dd>
                            {comparison.cost.cost_usd === null
                                ? 'Not recorded'
                                : `$${comparison.cost.cost_usd.toFixed(4)}`}
                            {comparison.cost.complete ? '' : ' (incomplete)'}
                        </dd>
                    </div>
                    <div>
                        <dt>Attempts</dt>
                        <dd>{comparison.attempt_count}</dd>
                    </div>
                </dl>
                {comparison.errors.map((error, index) => (
                    <Alert key={index} variant="destructive">
                        <AlertCircle />
                        <AlertDescription>
                            {error['type']}: {error['message']}
                        </AlertDescription>
                    </Alert>
                ))}
                {comparison.transcript_truncated ? (
                    <p className="text-muted-foreground text-xs">
                        Recorded transcript truncated.
                    </p>
                ) : null}
                <div className="min-w-0 space-y-1">
                    {comparison.transcript.length === 0 ? (
                        <p className="text-muted-foreground text-sm">
                            No LLM items recorded
                        </p>
                    ) : (
                        comparison.transcript.map(renderItem)
                    )}
                </div>
                <details className="text-muted-foreground text-xs">
                    <summary className="cursor-pointer">
                        Execution metadata
                    </summary>
                    <pre className="bg-muted max-h-64 overflow-auto rounded p-2 break-words whitespace-pre-wrap">
                        {JSON.stringify(
                            {
                                code_version:
                                    comparison.candidate_outcome.code_version,
                                decision: comparison.candidate_outcome.decision,
                                sample_rate: comparison.sample_rate,
                                cost: comparison.cost,
                                proposals: comparison.proposals,
                                refusals: comparison.refusals,
                                uncertainties: comparison.uncertainties,
                                receipts_truncated:
                                    comparison.receipts_truncated,
                                timings: comparison.timings,
                                attempts: comparison.attempts,
                            },
                            null,
                            2,
                        )}
                    </pre>
                </details>
                {comparison.trace_links
                    .filter(
                        (link) =>
                            link.label === 'Agent trace (legacy reply history)',
                    )
                    .map((link) => (
                        <a
                            key={link.href}
                            href={link.href}
                            className="inline-flex items-center gap-1 text-sm underline underline-offset-2"
                        >
                            {link.label} <ExternalLink className="size-3" />
                        </a>
                    ))}
            </CardContent>
        </Card>
    );
}
